import { IOption } from '@survey/typing'
import { BaseUserModel } from './typing'

export type Action =
  | {
      type: 'LOAD_USER'
      payload: { value: BaseUserModel }
    }
  | {
      type: 'UPDATE_RESPONSE'
      payload: {
        sectionId: string
        questionId: string
        response: IOption[]
        branchingResponse?: IOption[]
      }
    }
  | {
      type: 'UPDATE_LAST_RESPONSE'
      payload: {
        sectionId: string
        questionId: string
        surveyKey?: string
      }
    }
  | {
      type: 'SET_IMPORTANT'
      payload: { sectionId: string; questionId: string; importance: 1 | 2 | 3 }
    }
  | {
      type: 'TOGGLE_SHOW_ON_PROFILE'
      payload: { sectionId: string; questionId: string }
    }
  | {
      type: 'SET_FREE_TEXT_RESPONSE'
      payload: {
        sectionId: string
        questionId: string
        freeText: string
        isBranchingQuestion?: boolean
      }
    }
  | {
      type: 'TOGGLE_OTHER_SELECTED'
      payload: { sectionId: string; questionId: string }
    }
function providerReducer<T extends BaseUserModel>(
  state: T,
  { payload, type }: Action
): T {
  switch (type) {
    case 'LOAD_USER':
      return {
        ...state,
        ...payload.value,
      }
    case 'UPDATE_RESPONSE':
      const allAnswers = state.answers || {} // TODO revisit why this is NULL
      if (!allAnswers[payload.sectionId]) {
        allAnswers[payload.sectionId] = {}
      }
      const categoryLevelAnswers = allAnswers[payload.sectionId]
      if (!categoryLevelAnswers[payload.questionId]) {
        categoryLevelAnswers[payload.questionId] = {
          sectionId: payload.sectionId,
          questionId: payload.questionId,
          response: {
            response: payload.response,
            branchingResponse: payload.branchingResponse || [],
            showOnMyProfile: false,
          },
        }
      }
      const questionLevelAnswers =
        categoryLevelAnswers[payload.questionId].response
      return {
        ...state,
        answers: {
          ...allAnswers,
          [payload.sectionId]: {
            ...categoryLevelAnswers,
            [payload.questionId]: {
              sectionId: payload.sectionId,
              questionId: payload.questionId,
              response: {
                ...questionLevelAnswers,
                response: [...payload.response],
                branchingResponse:
                  payload.branchingResponse ||
                  questionLevelAnswers.branchingResponse ||
                  [],
              },
            },
          },
        },
      }
    case 'UPDATE_LAST_RESPONSE': {
      // Store lastResponse keyed by the active survey ID so that LoadPatient
      // can look it up via user.lastResponse?.[surveyId].
      const surveyKey =
        payload.surveyKey ||
        state.activeSurvey ||
        Object.keys(state.surveyState || {})[0]
      return {
        ...state,
        lastResponse: surveyKey
          ? {
              ...(state.lastResponse || {}),
              [surveyKey]: {
                sectionId: payload.sectionId,
                questionId: payload.questionId,
              },
            }
          : {
              sectionId: payload.sectionId,
              questionId: payload.questionId,
            },
      }
    }
    case 'SET_IMPORTANT':
      const allAnswersSi = state.answers || {}
      if (!allAnswersSi[payload.sectionId]) {
        allAnswersSi[payload.sectionId] = {}
      }
      const categoryLevelAnswersSi = allAnswersSi[payload.sectionId]
      if (!categoryLevelAnswersSi[payload.questionId]) {
        categoryLevelAnswersSi[payload.questionId] = {
          sectionId: payload.sectionId,
          questionId: payload.questionId,
          response: {
            response: [],
            showOnMyProfile: false,
            importance: payload.importance,
          },
        }
      }
      const questionLevelAnswersSi =
        categoryLevelAnswersSi[payload.questionId].response
      return {
        ...state,
        answers: {
          ...allAnswersSi,
          [payload.sectionId]: {
            ...categoryLevelAnswersSi,
            [payload.questionId]: {
              sectionId: payload.sectionId,
              questionId: payload.questionId,
              response: {
                ...questionLevelAnswersSi,
                importance: payload.importance,
              },
            },
          },
        },
      }
    case 'TOGGLE_SHOW_ON_PROFILE':
      const allAnswersSp = state.answers
      if (!allAnswersSp[payload.sectionId]) {
        allAnswersSp[payload.sectionId] = {}
      }
      const categoryLevelAnswersSp = allAnswersSp[payload.sectionId]
      if (!categoryLevelAnswersSp[payload.questionId]) {
        categoryLevelAnswersSp[payload.questionId] = {
          sectionId: payload.sectionId,
          questionId: payload.questionId,
          response: {
            response: [],
            showOnMyProfile: false,
          },
        }
      }
      const questionLevelAnswersSp =
        categoryLevelAnswersSp[payload.questionId].response
      return {
        ...state,
        answers: {
          ...allAnswersSp,
          [payload.sectionId]: {
            ...categoryLevelAnswersSp,
            [payload.questionId]: {
              sectionId: payload.sectionId,
              questionId: payload.questionId,
              response: {
                ...questionLevelAnswersSp,
                showOnMyProfile:
                  !categoryLevelAnswersSp[payload.questionId].response
                    .showOnMyProfile,
              },
            },
          },
        },
      }
    case 'TOGGLE_OTHER_SELECTED':
      const allAnswersTo = state.answers || {}
      if (!allAnswersTo[payload.sectionId]) {
        allAnswersTo[payload.sectionId] = {}
      }
      const categoryLevelAnswersTo = allAnswersTo[payload.sectionId]
      if (!categoryLevelAnswersTo[payload.questionId]) {
        categoryLevelAnswersTo[payload.questionId] = {
          sectionId: payload.sectionId,
          questionId: payload.questionId,
          response: {
            response: [],
            showOnMyProfile: false,
            otherSelected: false,
          },
        }
      }
      const questionLevelAnswersTo =
        categoryLevelAnswersTo[payload.questionId].response
      const prevOtherSelected = questionLevelAnswersTo.otherSelected ?? false
      return {
        ...state,
        answers: {
          ...allAnswersTo,
          [payload.sectionId]: {
            ...categoryLevelAnswersTo,
            [payload.questionId]: {
              sectionId: payload.sectionId,
              questionId: payload.questionId,
              response: {
                ...questionLevelAnswersTo,
                otherSelected: !prevOtherSelected,
              },
            },
          },
        },
      }
    case 'SET_FREE_TEXT_RESPONSE':
      const allAnswersFr = state.answers || {}
      if (!allAnswersFr[payload.sectionId]) {
        allAnswersFr[payload.sectionId] = {}
      }
      const categoryLevelAnswersFr = allAnswersFr[payload.sectionId]
      if (!categoryLevelAnswersFr[payload.questionId]) {
        categoryLevelAnswersFr[payload.questionId] = {
          sectionId: payload.sectionId,
          questionId: payload.questionId,
          response: {
            response: [],
            showOnMyProfile: false,
            freeTextResponse: payload.freeText,
          },
        }
      }
      const questionLevelAnswersFr =
        categoryLevelAnswersFr[payload.questionId].response
      return {
        ...state,
        answers: {
          ...allAnswersFr,
          [payload.sectionId]: {
            ...categoryLevelAnswersFr,
            [payload.questionId]: {
              sectionId: payload.sectionId,
              questionId: payload.questionId,
              response: {
                ...questionLevelAnswersFr,
                freeTextResponse: payload.freeText,
              },
            },
          },
        },
      }
    default:
      throw new Error('Invalid action type')
  }
}

export default providerReducer
